import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { $ } from '../lib/api';
import { fmtDateTime, fmtDateShort } from '../lib/format';
import { Modal, avatarSlot, Pill, PillTone, Btn } from './ui';

// P0-1 修：合同签署状态(esignStatus) → 中文标签
const ESIGN_LABEL: Record<string, string> = {
  draft: '草稿', sent: '待签字', customer_signed: '待签字', all_signed: '已签订', voided: '已作废',
};

interface Props {
  open: boolean;
  customerId: number | null;
  onClose: () => void;
}

interface StepData {
  num: number;
  title: string;
  date?: string;
  pillTone?: PillTone;
  pillLabel?: string;
  rows: Array<{ label: string; value: string; tone?: 'success' | 'amount' | 'primary' }>;
  details?: string[];
}

/**
 * Build the 8 journey steps from customer + follow-up + business-doc data.
 * Renders L1 → L8 even when data is partial (steps without data fall back to a "等待中" placeholder).
 */
function buildSteps(c: any, follows: any[], order?: any): StepData[] {
  const byStage = (st: string) => follows.filter((f) => (f.stage ?? '').toUpperCase().startsWith(st));
  const fmt = (d?: string) => (d ? fmtDateTime(d) : '—');
  /**
   * Step completion is derived from concrete customer fields, NOT currentStage.
   * Previous bug: using `currentStage.startsWith('L8')` made ALL L8-tagged steps
   * (6/7/8) show as "进行中" simultaneously, even when underlying data was done.
   * This map encodes the actual business state of each of the 8 stages.
   */
  const stepDone = {
    1: !!c.createdAt,                                                  // 客户管理 = 客户已建档
    2: !!c.firstFollowupAt || follows.length > 0,                       // 跟进记录 = 至少 1 条跟进
    3: !!c.hasContract,                                                 // 签订合同
    4: !!c.hasInvoice || c.invoiceStatus === 'issued',                  // 发票管理
    5: Number(c.totalPaid ?? 0) > 0,                                    // 定金收账
    6: c.shipmentStatus === 'delivered',                                // 教材邮寄
    7: c.materialsStatus === 'approved',                                // 学员资料
    8: order?.approvalStatus === 'approved' || c.status === 'in_service', // 提交订单
  } as Record<number, boolean>;
  /**
   * "current step" = the FIRST step not yet done. Everything before is success,
   * everything after is default (待开始). The current step itself shows primary "进行中".
   */
  const firstUndoneStep = (() => {
    for (let i = 1; i <= 8; i++) if (!stepDone[i]) return i;
    return 9; // all 8 done
  })();
  const stepPill = (stepNum: number, doneLabel = '已完成'): { pillTone?: PillTone; pillLabel?: string } => {
    if (stepDone[stepNum]) return { pillTone: 'success', pillLabel: doneLabel };
    if (stepNum === firstUndoneStep) return { pillTone: 'primary', pillLabel: '进行中' };
    return { pillTone: 'default', pillLabel: '待开始' };
  };

  return [
    {
      num: 1,
      title: '客户管理',
      date: c.createdAt ? fmtDateShort(c.createdAt) : undefined,
      ...stepPill(1),
      rows: [
        { label: '客户来源', value: c.source ?? '—' },
        { label: '分配方式', value: '主管派单' },
        { label: '微信状态', value: c.wechatStatus === 'accepted' ? '已通过' : (c.wechatStatus ?? '未加'), tone: c.wechatStatus === 'accepted' ? 'success' : undefined },
        { label: '接收响应', value: c.assignmentAcceptedAt && c.assignedAt
          ? `${Math.round((dayjs(c.assignmentAcceptedAt).valueOf() - dayjs(c.assignedAt).valueOf()) / 60000)} 分钟` : '—' },
      ],
      details: c.notes ? [c.notes] : [],
    },
    {
      num: 2,
      title: '跟进记录',
      date: c.firstFollowupAt && c.lastFollowupAt
        ? `${fmtDateShort(c.firstFollowupAt)} — ${fmtDateShort(c.lastFollowupAt)}`
        : (c.firstFollowupAt ? fmtDateShort(c.firstFollowupAt) : undefined),
      ...stepPill(2),
      rows: [
        { label: '跟进天数', value: c.firstFollowupAt ? `${dayjs().diff(dayjs(c.firstFollowupAt), 'day')} 天` : '—' },
        { label: '跟进次数', value: `${follows.length} 次` },
        { label: '健康度', value: `${c.healthScore ?? 0} / 100`, tone: (c.healthScore ?? 0) >= 70 ? 'success' : undefined },
        { label: '当前阶段', value: c.currentStage ?? '—' },
      ],
      details: follows.slice(0, 5).map((f) =>
        `${f.stage ?? '·'} ${fmtDateShort(f.createdAt)} · ${(f.aiSummary || f.content || '').slice(0, 80)}`,
      ),
    },
    {
      num: 3,
      title: '签订合同',
      date: c.contractSignedAt ? fmtDateShort(c.contractSignedAt) : undefined,
      // A1：第3步 chip 跟 esignStatus 走（同详情行口径：sent/customer_signed→待签字、all_signed→已签订），
      // 有 esign 状态时不再仅凭 hasContract 布尔显「已签订」；无 esign 状态才回退到原 stepPill（hasContract/进行中/待开始）。
      ...(c.esignStatus
        ? { pillTone: (c.esignStatus === 'all_signed' ? 'success' : 'primary') as PillTone, pillLabel: ESIGN_LABEL[c.esignStatus] ?? c.esignStatus }
        : stepPill(3, '已签订')),
      rows: [
        { label: '合同状态',
          value: c.esignStatus ? (ESIGN_LABEL[c.esignStatus] ?? c.esignStatus) : (c.hasContract ? '已签订' : '未签'),
          tone: c.esignStatus === 'all_signed' || (!c.esignStatus && c.hasContract) ? 'success' : (c.esignStatus ? 'primary' : undefined) },
        { label: '合同金额', value: Number(c.totalDue) > 0 ? `¥ ${Number(c.totalDue).toLocaleString()}` : '—', tone: 'amount' },
      ],
    },
    {
      num: 4,
      title: '发票管理',
      date: c.invoiceIssuedAt ? fmtDateShort(c.invoiceIssuedAt) : undefined,
      ...stepPill(4, '已开票'),
      rows: [
        { label: '发票状态', value: c.invoiceStatus ?? '—' },
        { label: '是否需要', value: c.hasInvoice ? '需要' : '不需要' },
      ],
    },
    {
      num: 5,
      title: '定金收账',
      ...stepPill(5, '已结清'),
      rows: [
        { label: '应收总额', value: Number(c.totalDue) > 0 ? `¥ ${Number(c.totalDue).toLocaleString()}` : '—', tone: 'amount' },
        { label: '已收金额', value: Number(c.totalPaid) > 0 ? `¥ ${Number(c.totalPaid).toLocaleString()}` : '—', tone: 'amount' },
        { label: '待收尾款', value: (Number(c.totalDue) - Number(c.totalPaid)) > 0 ? `¥ ${(Number(c.totalDue) - Number(c.totalPaid)).toLocaleString()}` : '—' },
      ],
    },
    {
      num: 6,
      title: '教材邮寄',
      ...stepPill(6, '已签收'),
      rows: [
        { label: '邮寄状态', value: c.shipmentStatus ?? '—' },
        { label: '地址', value: c.region ?? '—' },
      ],
    },
    {
      num: 7,
      title: '学员资料',
      ...stepPill(7, 'OA 已通过'),
      rows: [
        { label: '资料状态', value: c.materialsStatus ?? '—' },
        { label: '完成度', value: c.materialsStatus === 'approved' ? '4/4 全部齐全' : '—', tone: 'success' },
      ],
    },
    {
      num: 8,
      title: '提交订单 · 销售闭环',
      ...stepPill(8, '已完成'),
      rows: [
        ...(order ? [
          { label: '订单编号', value: order.orderCode ?? '—' as string },
          { label: '报名班型', value: order.classType ?? '—', tone: 'success' as const },
          { label: '订单金额', value: Number(order.amount) > 0 ? `¥ ${Number(order.amount).toLocaleString()}` : '—', tone: 'amount' as const },
          { label: '销售提成', value: Number(order.commission) > 0 ? `¥ ${Number(order.commission).toLocaleString()}` : '—', tone: 'primary' as const },
          { label: '审批状态', value: order.approvalStatus ?? '—' },
        ] : [
          { label: '订单状态', value: c.orderStatus ?? '—' },
          { label: '订单金额', value: Number(c.totalPaid) > 0 ? `¥ ${Number(c.totalPaid).toLocaleString()}` : '—', tone: 'amount' as const },
        ]),
      ],
      details: c.status === 'in_service' || (order && order.approvalStatus === 'approved')
        ? ['✨ 销售闭环完成']
        : [],
    },
  ];
}

export function JourneyModal({ open, customerId, onClose }: Props) {
  const { data: c } = useQuery({
    queryKey: ['customer', String(customerId)],
    queryFn: () => $.get<any>(`/customers/${customerId}`),
    enabled: open && !!customerId,
  });
  const { data: follows } = useQuery({
    queryKey: ['follow-ups', customerId, 'journey'],
    queryFn: () => $.get<{ items: any[] }>(`/follow-ups`, { customerId, pageSize: 100 }),
    enabled: open && !!customerId,
  });
  /** Latest order for class_type/commission — used in summary + step 8. */
  const { data: orderRes } = useQuery({
    queryKey: ['orders', customerId, 'journey'],
    queryFn: () => $.get<{ items: any[] }>(`/orders`, { customerId, pageSize: 1 }),
    enabled: open && !!customerId,
  });
  const order = orderRes?.items?.[0];

  if (!open || !customerId) return null;
  if (!c) {
    return (
      <Modal open={true} title="客户旅程" size="large" onClose={onClose}>
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</div>
      </Modal>
    );
  }

  const steps = buildSteps(c, follows?.items ?? [], order);
  const slot = avatarSlot(c.id);
  const trackingDays = c.firstFollowupAt
    ? dayjs().diff(dayjs(c.firstFollowupAt), 'day')
    : c.createdAt ? dayjs().diff(dayjs(c.createdAt), 'day') : 0;

  const reachedCount = steps.filter((s) => s.pillTone === 'success').length;
  const trackingDaysTotal = c.createdAt ? dayjs().diff(dayjs(c.createdAt), 'day') : 0;

  return (
    <Modal
      open={true}
      title={
        <>
          客户完整旅程
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 8 }}>
            {trackingDaysTotal > 0 && `共 ${trackingDaysTotal} 天 · `}
            {reachedCount} / 8 步已完成
          </span>
        </>
      }
      size="large"
      onClose={onClose}
      bodyClass="journey-modal-body"
      footer={
        <>
          <Btn kind="default" onClick={onClose}>关闭</Btn>
          <Btn kind="primary">导出旅程报告</Btn>
        </>
      }
    >
      <div className="journey-summary">
        <div className="journey-sum-row">
          <div className={`journey-sum-avatar`} style={{ background: avatarBgFromSlot(slot) }}>
            {c.nickname?.charAt(0)}
          </div>
          <div className="journey-sum-info">
            <div className="journey-sum-name">
              {c.nickname}
              <span className="cust-name-flag flag-journey">完整旅程</span>
            </div>
            <div className="journey-sum-meta">
              {[c.age && `${c.age} 岁`, c.gender, c.region].filter(Boolean).join(' · ')}
              {' · '}来源：<strong>{c.source ?? '—'}</strong>
              {c.realName && <> · {c.realName}</>}
            </div>
          </div>
        </div>
        <div className="journey-sum-stats">
          <div>
            <div className="journey-sum-stat-label">订单金额</div>
            <div className="journey-sum-stat-value success">
              ¥ {Number(order?.amount ?? c.totalPaid ?? c.totalDue ?? 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="journey-sum-stat-label">报名班型</div>
            <div className="journey-sum-stat-value">{order?.classType ?? '—'}</div>
          </div>
          <div>
            <div className="journey-sum-stat-label">销售提成</div>
            <div className="journey-sum-stat-value primary">
              ¥ {Number(order?.commission ?? Math.round(Number(c.totalPaid ?? 0) * 0.1)).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="journey-sum-stat-label">全程用时</div>
            <div className="journey-sum-stat-value">{trackingDays} 天</div>
          </div>
        </div>
      </div>
      <div className="journey-steps">
        {steps.map((s) => {
          const isFuture = s.pillTone === 'default';
          return (
            <div key={s.num} className="journey-step" style={isFuture ? { opacity: 0.55 } : undefined}>
              <div
                className="journey-step-icon"
                style={isFuture ? { background: 'var(--text-4)', boxShadow: '0 0 0 1px var(--text-4)' } : undefined}
              >
                {s.num}
              </div>
              <div className="journey-step-card">
                <div className="journey-step-head">
                  <span className="journey-step-title">{s.title}</span>
                  {s.pillTone && s.pillLabel && (
                    <span style={{ marginLeft: 6 }}>
                      <Pill tone={s.pillTone}>{s.pillLabel}</Pill>
                    </span>
                  )}
                  {s.date && <span className="journey-step-date">{s.date}</span>}
                </div>
                <div className="journey-step-grid">
                  {s.rows.map((r, i) => (
                    <div key={i}>
                      <span className="label">{r.label}</span>
                      <span className={`value ${r.tone === 'success' ? 'success' : r.tone === 'amount' ? 'amount' : ''}`}>{r.value}</span>
                    </div>
                  ))}
                </div>
                {s.details && s.details.filter(Boolean).length > 0 && (
                  <div className="journey-detail-list">
                    {s.details.filter(Boolean).map((d, i) => (
                      <div key={i} className="journey-detail-item">{d}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function avatarBgFromSlot(slot: number) {
  const map: Record<number, string> = {
    1: 'linear-gradient(135deg, #6294f5, #3370ff)',
    2: 'linear-gradient(135deg, #51cf8a, #34c759)',
    3: 'linear-gradient(135deg, #d6649e, #c44569)',
    4: 'linear-gradient(135deg, #f6c155, #f0a020)',
    5: 'linear-gradient(135deg, #9b7dd6, #7c4dff)',
    6: 'linear-gradient(135deg, #ee7065, #ee5253)',
    7: 'linear-gradient(135deg, #5eb6e0, #3aafda)',
    8: 'linear-gradient(135deg, #95cc6c, #76b639)',
  };
  return map[slot] ?? map[1];
}
